import { z } from 'zod';

export const catalogSlugSchema = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const catalogCursorSchema = z.string().min(1).max(2048);
export const catalogLimitSchema = z.number().int().min(1).max(100);
export const categoryListInputSchema = z.object({
  limit: catalogLimitSchema.optional(),
  cursor: catalogCursorSchema.optional(),
});
export const productListInputSchema = categoryListInputSchema.extend({
  category: catalogSlugSchema.optional(),
  productType: z.enum(['wood-engraving', 'ornament', 'tshirt']).optional(),
  featured: z.boolean().optional(),
  badge: z.enum(['bestseller', 'new', 'popular', 'seasonal']).optional(),
  sort: z.enum(['featured', 'newest']).optional(),
});

export const moneySchema = z.object({
  amountMinor: z.number().int().nonnegative(),
  currency: z.literal('USD'),
});
export const mediaSchema = z.object({
  id: z.string().min(1),
  kind: z.literal('image'),
  role: z.enum(['primary', 'gallery', 'thumbnail', 'customization-preview']),
  objectKey: z
    .string()
    .min(1)
    .max(512)
    .regex(/^catalog\/(?:products|categories|placeholders)\/[A-Za-z0-9][A-Za-z0-9._/-]*$/)
    .refine((key) => !key.includes('..') && !key.includes('\\') && !key.includes('://')),
  altText: z.string().min(1),
  displayOrder: z.number().int(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  contentType: z.string().min(1).optional(),
});
const seoSchema = z.object({ title: z.string().optional(), description: z.string().optional() });
export const publicCategorySchema = z.object({
  id: z.string().min(1),
  slug: catalogSlugSchema,
  name: z.string().min(1),
  shortDescription: z.string().min(1),
  displayOrder: z.number().int(),
  heroMedia: mediaSchema.optional(),
  seo: seoSchema,
});
export const publicProductSummarySchema = z.object({
  id: z.string().min(1),
  slug: catalogSlugSchema,
  categorySlug: catalogSlugSchema,
  productType: z.enum(['wood-engraving', 'ornament', 'tshirt']),
  name: z.string().min(1),
  shortDescription: z.string().min(1),
  startingPrice: moneySchema,
  compareAtPrice: moneySchema.optional(),
  badges: z.array(z.enum(['bestseller', 'new', 'popular', 'seasonal'])),
  featured: z.boolean(),
  customizable: z.boolean(),
  primaryMedia: mediaSchema,
});
const optionValueSchema = z.object({
  id: z.string().min(1),
  value: z.string().min(1),
  label: z.string().min(1),
  displayOrder: z.number().int(),
  priceAdjustment: moneySchema.optional(),
  active: z.boolean(),
});
const optionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  label: z.string().min(1),
  required: z.boolean(),
  displayOrder: z.number().int(),
  values: z.array(optionValueSchema),
});
const customizationSchema = z.object({
  mode: z.enum(['none', 'wood-engraving', 'ornament', 'tshirt-print']),
  textInputs: z.array(
    z.object({
      id: z.string().min(1),
      label: z.string().min(1),
      required: z.boolean(),
      maxLength: z.number().int().positive(),
      multiline: z.boolean(),
      placeholder: z.string().optional(),
    }),
  ),
  imageUpload: z
    .object({
      required: z.boolean(),
      acceptedContentTypes: z.array(z.string().min(1)),
      maxFiles: z.number().int().positive(),
      maxFileSizeBytes: z.number().int().positive(),
    })
    .optional(),
  designArea: z
    .object({
      width: z.number().positive(),
      height: z.number().positive(),
      unit: z.literal('px'),
      safeAreaInset: z.number().nonnegative().optional(),
    })
    .optional(),
  customerNotesAllowed: z.boolean(),
});
export const publicProductDetailSchema = publicProductSummarySchema.extend({
  description: z.string().min(1),
  media: z.array(mediaSchema),
  options: z.array(optionSchema),
  customization: customizationSchema,
  seo: seoSchema,
});
export const pageSchema = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() });
export const envelopeSchema = <T extends z.ZodType>(data: T) =>
  z.object({ data, requestId: z.string().min(1) });
